import React from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from '../../util/reactIntl';
import {
  getFavouriteIds,
  isFavourite,
  toggleFavourite,
  MAX_FAVOURITES,
} from '../../util/garageFavourites';
import { saveFavourites } from '../../ducks/garageFavourites.duck';
import NamedLink from '../NamedLink/NamedLink';
import css from './FavouriteToggle.module.css';

/**
 * Save or unsave a vehicle to the customer's ranking.
 *
 * Saving is a convenience, not a gate: a signed-out visitor sees a sign-in
 * link and nothing else is asked of them. Saving the vehicle adds it to the
 * bottom of the ranking; the customer orders it on the Saved Vehicles page.
 */
const FavouriteToggle = props => {
  const { listingId, compact = false, className } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const rootClassName = [css.root, className].filter(Boolean).join(' ');

  // Read defensively: this control sits on the listing page, and a page that
  // renders with a partial store must still render, just without saving.
  const isAuthenticated = useSelector(state => Boolean(state.auth?.isAuthenticated));
  const currentUser = useSelector(state => state.user?.currentUser);
  const saveInProgress = useSelector(state => Boolean(state.garageFavourites?.saveInProgress));
  const saveError = useSelector(state => state.garageFavourites?.saveError);

  if (!isAuthenticated) {
    return (
      <div className={rootClassName}>
        <NamedLink
          name="LoginPage"
          to={{ state: { from: `${location.pathname}${location.search}${location.hash}` } }}
          className={compact ? css.compactSignIn : css.signIn}
        >
          {compact ? <span aria-hidden="true">♡ </span> : null}
          <FormattedMessage id="FavouriteToggle.signInToSave" />
        </NamedLink>
      </div>
    );
  }

  const ids = getFavouriteIds(currentUser);
  const saved = isFavourite(ids, listingId);
  const full = !saved && ids.length >= MAX_FAVOURITES;

  const handleClick = () => {
    const next = toggleFavourite(ids, listingId);
    if (next.length === ids.length && !saved) return;
    dispatch(saveFavourites(next));
  };

  return (
    <div className={rootClassName}>
      <button
        type="button"
        className={saved ? css.buttonSaved : css.button}
        aria-pressed={saved}
        onClick={handleClick}
        disabled={saveInProgress || full}
      >
        {compact ? <span aria-hidden="true">{saved ? '♥ ' : '♡ '}</span> : null}
        <FormattedMessage
          id={
            compact
              ? saved
                ? 'FavouriteToggle.savedShort'
                : 'FavouriteToggle.saveShort'
              : saved
              ? 'FavouriteToggle.saved'
              : 'FavouriteToggle.save'
          }
          defaultMessage={compact ? (saved ? 'Saved' : 'Save') : undefined}
        />
      </button>
      {full ? (
        <p className={css.note} role="status">
          <FormattedMessage id="FavouriteToggle.full" values={{ max: MAX_FAVOURITES }} />
        </p>
      ) : null}
      {saveError ? (
        <p className={css.note} role="alert">
          <FormattedMessage id="FavouriteToggle.saveError" />
        </p>
      ) : null}
    </div>
  );
};

export default FavouriteToggle;
